// ─── Impostazioni → Maestri ──────────────────────────────────────────────────
//
// 📌 Un maestro prima si spegne («Metti a riposo»), poi si può eliminare
// (🗑️ 18/10/2026, mockup «Elimina un maestro», A): le sue lezioni già fatte e i
// suoi conti restano col suo nome, lui sparisce dall'elenco, dal calendario e dal
// bot. Con lezioni ancora in programma il server dice quante, e no.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  GIORNI,
  ORDINE_SETTIMANA,
  riassuntoOreRiservate,
  type BandaMaestro,
  type Campo as CampoDiGioco,
  type MaestroDiSegreteria,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { perGiorni } from '../../lib/esiti.js';
import { oggi, giornoPerEsteso } from '../../lib/time.js';
import { Attesa, Campo, Guasto, Vuoto } from '../../components/pezzi.js';
import { CampoOra } from '../../components/CampoOra.js';
import { CampoData } from '../../components/CampoData.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'maestri';

/**
 * I gruppi di giorni che si spuntano in un colpo solo.
 *
 * ⚖️ Sono **tre e non di più**: sono i tre modi in cui un maestro lavora
 * davvero (tutta la settimana, i feriali, il fine settimana). Un elenco di
 * scorciatoie più lungo delle sette caselle che riempie sarebbe più lento da
 * leggere di quello che accorcia.
 * 🚨 I numeri sono quelli di Postgres — 0 = domenica (⇒ `GIORNI` in
 * `packages/shared/src/circolo.ts`, dove l'indice È il dato).
 */
const GRUPPI_DI_GIORNI: ReadonlyArray<{ nome: string; giorni: readonly number[] }> = [
  { nome: 'Tutti i giorni', giorni: ORDINE_SETTIMANA },
  { nome: 'Lun–ven', giorni: [1, 2, 3, 4, 5] },
  { nome: 'Sab e dom', giorni: [6, 0] },
];

export function Maestri(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const elenco = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<{ items: MaestroDiSegreteria[] }>('GET', '/settings/instructors'),
  });

  if (elenco.isLoading) return <Attesa cosa="i maestri" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;
  const items = elenco.data?.items ?? [];

  return (
    <section>
      <header className="testata">
        <h2>Maestri</h2>
        <p className="tenue">
          {items.filter((m) => m.active).length} attivi su {items.length}.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      {items.length === 0 ? (
        <Vuoto titolo="Nessun maestro" perché="Le lezioni hanno bisogno di qualcuno che le tenga." />
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Ordine</th>
                <th>Stato</th>
                <th>Accesso</th>
                <th>Ore riservate</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <Riga key={m.id} maestro={m} modificabile={modificabile} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modificabile && <Nuovo />}
    </section>
  );
}

function Riga({
  maestro,
  modificabile,
}: {
  maestro: MaestroDiSegreteria;
  modificabile: boolean;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  /**
   * 🗣️ *«anche qui manca il modifica»* (17/09/2026). Il server accettava già
   * nome e ordine (`modificaMaestroSchema`): mancava solo il modo di
   * chiederglielo, e un maestro scritto con un refuso si poteva solo mettere a
   * riposo e riscrivere da capo — lasciando in elenco due righe per la stessa
   * persona.
   * ⚠️ Il pannello si apre in una RIGA SUA sotto, non dentro una cella: un
   *    modulo dentro un `<td>` allargherebbe la colonna e sfascerebbe la tabella.
   */
  const [aperto, setAperto] = useState(false);
  /** 🎓 18/10/2026: il riquadro «🎓 Ore riservate», aperto dal suo bottone sulla riga. */
  const [riservate, setRiservate] = useState(false);
  /** 🗑️ 18/10/2026: la domanda «Eliminare …?», nella riga sua sotto. */
  const [chiedo, setChiedo] = useState(false);
  const ore = useQuery({
    // ⚙️ Comincia per `maestri`: `usaRilettura(CHIAVE)` rilegge anche questa.
    queryKey: [CHIAVE, maestro.id, 'ore-riservate'],
    queryFn: () =>
      chiama<{ items: BandaMaestro[] }>('GET', `/settings/instructors/${maestro.id}/ore-riservate`),
  });
  const campi = usaCampi();
  const cambia = useMutation({
    meta: {
      fatto: (_d, c) =>
        (c as { active: boolean }).active
          ? `${maestro.displayName} riattivato`
          : `${maestro.displayName} disattivato`,
    },
    mutationFn: (c: { active: boolean }) => chiama('PATCH', `/settings/instructors/${maestro.id}`, c),
    onSuccess: rileggi,
  });
  const elimina = useMutation({
    meta: { fatto: `${maestro.displayName} eliminato` },
    mutationFn: () => chiama('DELETE', `/settings/instructors/${maestro.id}`),
    onSuccess: () => {
      setChiedo(false);
      rileggi();
    },
  });

  return (
    <>
      <tr className={maestro.active ? '' : 'spento'}>
        <td>{maestro.displayName}</td>
        <td>{maestro.sortOrder}</td>
        <td>{maestro.active ? 'attivo' : 'non più'}</td>
        <td>
          <StatoAccesso maestro={maestro} />
        </td>
        <td className="piccolo">
          {ore.data
            ? riassuntoOreRiservate(ore.data.items, (id) => nomeDelCampo(campi.data?.items ?? [], id), oggi())
            : '—'}
        </td>
        <td className="azioni">
          {modificabile && (
            <>
              <button type="button" aria-expanded={riservate} onClick={() => setRiservate((v) => !v)}>
                🎓 Ore riservate
              </button>
              <button
                type="button"
                className="piatto"
                aria-expanded={aperto}
                onClick={() => setAperto((v) => !v)}
              >
                Modifica
              </button>
              <button
                type="button"
                className="piatto"
                disabled={cambia.isPending}
                onClick={() => cambia.mutate({ active: !maestro.active })}
              >
                {maestro.active ? 'Metti a riposo' : 'Rimetti in servizio'}
              </button>
              {/* 🗑️ Solo a riposo: così non si elimina per sbaglio un maestro al lavoro. */}
              {!maestro.active && (
                <button
                  type="button"
                  className="piatto pericolo"
                  aria-expanded={chiedo}
                  onClick={() => setChiedo((v) => !v)}
                >
                  🗑️ Elimina
                </button>
              )}
            </>
          )}
          <Guasto errore={cambia.error} />
        </td>
      </tr>
      {modificabile && chiedo && !maestro.active && (
        <tr className="riga-modifica">
          <td colSpan={6}>
            <div className="conferma">
              {maestro.lessonsAhead > 0 ? (
                <p className="tenue piccolo">
                  <strong>{maestro.displayName}</strong> ha ancora {maestro.lessonsAhead}{' '}
                  {maestro.lessonsAhead === 1 ? 'lezione' : 'lezioni'} in programma. Spostale a un altro
                  maestro o disdicile dal calendario, poi potrai eliminarlo.
                </p>
              ) : (
                <p className="tenue piccolo">
                  Eliminare <strong>{maestro.displayName}</strong>? Sparisce dall’elenco, dal calendario e dal
                  bot.{' '}
                  {maestro.lessonsDone > 0
                    ? `Le sue ${maestro.lessonsDone === 1 ? 'lezione già fatta' : `${maestro.lessonsDone} lezioni già fatte`} e i suoi conti restano, col suo nome.`
                    : 'Non ha mai tenuto lezioni: sparisce del tutto.'}
                </p>
              )}
              <Guasto errore={elimina.error} />
              {maestro.lessonsAhead === 0 && (
                <button
                  type="button"
                  className="pericolo"
                  disabled={elimina.isPending}
                  onClick={() => elimina.mutate()}
                >
                  {elimina.isPending ? 'Un attimo…' : 'Sì, elimina'}
                </button>
              )}
              <button type="button" className="piatto" onClick={() => setChiedo(false)}>
                {maestro.lessonsAhead > 0 ? 'Ho capito' : 'Lascia stare'}
              </button>
            </div>
          </td>
        </tr>
      )}
      {modificabile && riservate && (
        <tr className="riga-modifica">
          <td colSpan={6}>
            <OreRiservate
              maestro={maestro}
              items={ore.data?.items ?? null}
              errore={ore.error}
              campi={campi.data?.items ?? []}
            />
          </td>
        </tr>
      )}
      {modificabile && aperto && (
        <tr className="riga-modifica">
          <td colSpan={6}>
            <div className="pannelli-riga">
              <Modifica maestro={maestro} chiudi={() => setAperto(false)} />
              <Accesso maestro={maestro} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Cambiare un maestro già in elenco.
 *
 * ⭐ Si manda SOLO quello che è cambiato davvero: `modificaMaestroSchema` dice
 * «non toccarlo» per ciò che non arriva, quindi rispedire tutto riscriverebbe
 * anche i valori che nessuno ha guardato.
 *
 * ⚠️ Lo STATO (attivo / a riposo) non sta qui: ha già il suo bottone nella
 * riga. Due posti per lo stesso gesto sono due posti da tenere d'accordo.
 */
function Modifica({
  maestro,
  chiudi,
}: {
  maestro: MaestroDiSegreteria;
  chiudi: () => void;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [nome, setNome] = useState(maestro.displayName);
  const [ordine, setOrdine] = useState(String(maestro.sortOrder));

  const cambiato: Record<string, unknown> = {};
  if (nome.trim() !== maestro.displayName) cambiato.displayName = nome.trim();
  if (Number(ordine) !== maestro.sortOrder) cambiato.sortOrder = Number(ordine);
  const niente = Object.keys(cambiato).length === 0 || nome.trim() === '';

  const salva = useMutation({
    meta: { fatto: `${maestro.displayName} salvato` },
    mutationFn: () => chiama('PATCH', `/settings/instructors/${maestro.id}`, cambiato),
    onSuccess: () => {
      rileggi();
      chiudi();
    },
  });

  return (
    <form
      className="pannello-fascia"
      onSubmit={(e) => {
        e.preventDefault();
        if (!niente) salva.mutate();
      }}
    >
      <div className="riga-campi">
        <Campo etichetta="Nome e cognome" required value={nome} onChange={(e) => setNome(e.target.value)} />
        <Campo
          etichetta="Ordine in elenco"
          type="number"
          min={0}
          max={999}
          required
          value={ordine}
          onChange={(e) => setOrdine(e.target.value)}
        />
      </div>
      <p className="tenue piccolo">
        Il nome è quello che si vede nella scheda della lezione. Per metterlo a riposo c’è il bottone nella
        riga.
      </p>
      <Guasto errore={salva.error} />
      <div className="azioni">
        <button type="submit" disabled={salva.isPending || niente}>
          {salva.isPending ? 'Un attimo…' : 'Salva'}
        </button>
        <button type="button" className="piatto" onClick={chiudi}>
          Annulla
        </button>
      </div>
    </form>
  );
}

/** Lo stato dell'accesso, in italiano: un'email nuda non dice se quella persona entra. */
function StatoAccesso({ maestro }: { maestro: MaestroDiSegreteria }): React.JSX.Element {
  const a = maestro.accesso;
  if (!a) return <span className="tenue">—</span>;
  /*
   * ⚠️ I tre stati sono DIVERSI e vogliono tre frasi diverse, o chi guarda
   * legge un'email e crede che quella persona stia entrando:
   *   · password non ancora scelta ⇒ l'accesso esiste ma **non entra**;
   *   · utente disattivato ⇒ la porta è chiusa da Utenti staff, non da qui;
   *   · tutto a posto ⇒ entra.
   */
  const nota = !a.active
    ? 'accesso disattivato'
    : !a.hasPassword
      ? 'deve ancora scegliere la password'
      : null;
  return (
    <span className={nota ? 'tenue' : undefined}>
      {a.email}
      {nota && <span className="piccolo"> — {nota}</span>}
    </span>
  );
}

/**
 * 🎾⭐ Aprire e chiudere l'accesso di un maestro.
 *
 * 🔒 Sta dietro `manage_users` e non dietro `manage_settings` come il resto di
 * questa schermata, perché **crea un accesso al gestionale**: il permesso si
 * chiede su ciò che il gesto fa, non su dove sta il bottone. Chi configura i
 * campi e i prezzi non apre per questo delle porte a delle persone.
 */
function Accesso({ maestro }: { maestro: MaestroDiSegreteria }): React.JSX.Element | null {
  const puoGestire = usaModifica('manage_users');
  const rileggi = usaRilettura(CHIAVE);
  const [email, setEmail] = useState('');

  const apri = useMutation({
    meta: { fatto: `accesso aperto per ${maestro.displayName}: la password la sceglie al primo accesso` },
    mutationFn: () => chiama('POST', `/settings/instructors/${maestro.id}/accesso`, { email: email.trim() }),
    onSuccess: () => {
      setEmail('');
      rileggi();
    },
  });
  const chiudi = useMutation({
    meta: { fatto: `accesso di ${maestro.displayName} chiuso` },
    mutationFn: () => chiama('DELETE', `/settings/instructors/${maestro.id}/accesso`),
    onSuccess: rileggi,
  });

  if (!puoGestire) return null;

  if (maestro.accesso) {
    return (
      <div className="riquadro">
        <h3>Accesso</h3>
        <p className="tenue piccolo">
          Entra con <strong>{maestro.accesso.email}</strong>.
          {!maestro.accesso.hasPassword &&
            ' Non ha ancora scelto la password: la sceglie da sé dalla schermata di accesso, con «È il mio primo accesso».'}
        </p>
        <Guasto errore={chiudi.error} />
        <button type="button" className="piatto" disabled={chiudi.isPending} onClick={() => chiudi.mutate()}>
          {chiudi.isPending ? 'Un attimo…' : 'Scollega questo accesso'}
        </button>
        {/* ⚖️ «Scollega», non «cancella», e la frase lo dice: si toglie il filo,
            la persona resta. Cancellare l'utente si fa da Utenti staff, ed è un
            altro gesto con altre conseguenze. */}
        <p className="tenue piccolo">
          Scollegare non cancella la persona: toglie solo il collegamento con questa scheda. L’accesso si
          disattiva da Impostazioni → Utenti staff.
        </p>
      </div>
    );
  }

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        if (email.trim() !== '') apri.mutate();
      }}
    >
      <h3>Apri l’accesso</h3>
      <p className="tenue piccolo">
        Il maestro entrerà con questa email e vedrà <strong>solo le sue lezioni</strong>: del gestionale della
        segreteria non vede niente. La password se la sceglie da sé — nessuno gliene assegna una.
      </p>
      <Campo
        etichetta="Email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Guasto errore={apri.error} />
      <button type="submit" disabled={apri.isPending || email.trim() === ''}>
        {apri.isPending ? 'Un attimo…' : 'Apri l’accesso'}
      </button>
    </form>
  );
}

function Nuovo(): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [nome, setNome] = useState('');

  const crea = useMutation({
    meta: { fatto: `maestro ${nome.trim()} aggiunto` },
    mutationFn: () => chiama('POST', '/settings/instructors', { displayName: nome, sortOrder: 0 }),
    onSuccess: () => {
      setNome('');
      rileggi();
    },
  });

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>Aggiungi un maestro</h3>
      <p className="tenue piccolo">
        Il nome è quello che si vede nella scheda della lezione: si scrive come lo chiamano i soci.
      </p>
      <Campo etichetta="Nome e cognome" required value={nome} onChange={(e) => setNome(e.target.value)} />
      <Guasto errore={crea.error} />
      <button type="submit" disabled={crea.isPending}>
        {crea.isPending ? 'Un attimo…' : 'Aggiungi'}
      </button>
    </form>
  );
}

/** I campi del circolo, per la colonna e per il riquadro: una lettura sola per tutta la pagina. */
function usaCampi() {
  return useQuery({
    queryKey: ['campi'],
    queryFn: () => chiama<{ items: CampoDiGioco[] }>('GET', '/settings/courts'),
  });
}

function nomeDelCampo(campi: readonly CampoDiGioco[], id: string): string {
  return campi.find((c) => c.id === id)?.name ?? 'campo tolto';
}

/** «09:00» ⇒ «9:00», come si legge. */
const ora = (t: string) => t.replace(/^0(\d)/, '$1');

/**
 * 🎓⭐⭐ LE ORE RISERVATE DI QUESTO MAESTRO — 18/10/2026, mockup «Ore riservate».
 *
 * 🗣️ Un posto solo, sulla riga del maestro: al posto di «Dove può prenotare»
 * (dentro «Modifica») e della linguetta «Lezioni» delle Fasce orarie. La
 * Segreteria riserva un **campo preciso** in certe ore, ogni settimana o un
 * giorno solo; lì le partite non si prenotano, le sue lezioni sì (anche dal bot).
 *
 * 🧩 Le righe di prima con «Tutti i campi» sono **da completare**: si sceglie il
 * campo, e da lì valgono. Finché non ce l'hanno non bloccano niente.
 *
 * 🔒 `manage_settings`, come le fasce del circolo.
 */
function OreRiservate({
  maestro,
  items,
  errore,
  campi,
}: {
  maestro: MaestroDiSegreteria;
  items: BandaMaestro[] | null;
  errore: unknown;
  campi: CampoDiGioco[];
}): React.JSX.Element | null {
  const puoGestire = usaModifica('manage_settings');
  const rileggi = usaRilettura(CHIAVE);
  const base = `/settings/instructors/${maestro.id}/ore-riservate`;

  const togli = useMutation({
    meta: { fatto: 'ore riservate tolte' },
    mutationFn: (id: string) => chiama('DELETE', `${base}/${id}`),
    onSuccess: rileggi,
  });
  const completa = useMutation({
    meta: { fatto: 'campo scelto: le ore sono riservate' },
    mutationFn: (c: { id: string; courtId: string }) =>
      chiama('PATCH', `${base}/${c.id}`, { courtId: c.courtId }),
    onSuccess: rileggi,
  });

  // ✏️ Una riga alla volta si apre in modifica, sul posto (18/10/2026).
  const [inModifica, setInModifica] = useState<string | null>(null);
  const modifica = useMutation({
    meta: { fatto: 'ore riservate modificate' },
    mutationFn: (m: { id: string; courtId: string; startsAtTime: string; endsAtTime: string }) =>
      chiama('PATCH', `${base}/${m.id}`, {
        courtId: m.courtId,
        startsAtTime: m.startsAtTime,
        endsAtTime: m.endsAtTime,
      }),
    onSuccess: () => {
      setInModifica(null);
      rileggi();
    },
  });

  if (!puoGestire) return null;

  const oggiQui = oggi();
  const vive = (items ?? []).filter((b) => b.active && (b.onDate === null || b.onDate >= oggiQui));
  const settimanali = vive.filter((b) => b.onDate === null);
  const giorniSoli = [...new Set(vive.filter((b) => b.onDate !== null).map((b) => b.onDate!))].sort();

  const voce = (b: BandaMaestro) => (
    <li key={b.id}>
      {b.courtId === null ? (
        <SceltaDelCampo
          b={b}
          campi={campi}
          inCorso={completa.isPending}
          scegli={(courtId) => completa.mutate({ id: b.id, courtId })}
        />
      ) : inModifica === b.id ? (
        <ModificaOre
          b={b}
          campi={campi}
          inCorso={modifica.isPending}
          salva={(m) => modifica.mutate({ id: b.id, ...m })}
          lascia={() => setInModifica(null)}
        />
      ) : (
        <span>
          {nomeDelCampo(campi, b.courtId)} · {ora(b.startsAtTime)}–{ora(b.endsAtTime)}
          {b.onDate !== null && <span className="tenue"> · solo quel giorno</span>}
        </span>
      )}
      {b.courtId !== null && inModifica !== b.id && (
        <button type="button" className="piatto" onClick={() => setInModifica(b.id)}>
          Modifica
        </button>
      )}
      <button type="button" className="piatto" disabled={togli.isPending} onClick={() => togli.mutate(b.id)}>
        Togli
      </button>
    </li>
  );

  return (
    <div className="riquadro">
      <h3>🎓 Ore riservate</h3>
      <p className="tenue piccolo">
        In queste ore il campo resta a <strong>{maestro.displayName}</strong> per le lezioni: le partite lì
        non si prenotano.
      </p>

      {items === null && !errore ? (
        <Attesa cosa="le ore riservate" />
      ) : vive.length === 0 ? (
        <p className="tenue piccolo">Ancora da aggiungere: le riservi qui sotto.</p>
      ) : (
        <ul className="elenco-bande">
          {ORDINE_SETTIMANA.filter((g) => settimanali.some((b) => b.weekday === g)).map((g) => (
            <li key={`g${g}`}>
              <strong>{GIORNI[g]}</strong>
              <ul>{settimanali.filter((b) => b.weekday === g).map(voce)}</ul>
            </li>
          ))}
          {giorniSoli.map((d) => (
            <li key={d}>
              <strong>{giornoPerEsteso(d)}</strong>
              <ul>{vive.filter((b) => b.onDate === d).map(voce)}</ul>
            </li>
          ))}
        </ul>
      )}
      <Guasto errore={errore} />
      <Guasto errore={togli.error} />
      <Guasto errore={completa.error} />
      <Guasto errore={modifica.error} />

      <RiservaAltreOre base={base} campi={campi} />
    </div>
  );
}

/** ✏️ La riga in modifica: campo, dalle, alle; il giorno resta quello. */
function ModificaOre({
  b,
  campi,
  inCorso,
  salva,
  lascia,
}: {
  b: BandaMaestro;
  campi: CampoDiGioco[];
  inCorso: boolean;
  salva(m: { courtId: string; startsAtTime: string; endsAtTime: string }): void;
  lascia(): void;
}): React.JSX.Element {
  const [campo, setCampo] = useState(b.courtId ?? '');
  const [inizio, setInizio] = useState(b.startsAtTime.slice(0, 5));
  const [fine, setFine] = useState(b.endsAtTime.slice(0, 5));
  const pronto = campo !== '' && inizio !== '' && fine !== '' && fine > inizio;
  return (
    <span className="in-modifica">
      <select aria-label="Campo" value={campo} onChange={(e) => setCampo(e.target.value)}>
        {campi
          .filter((c) => c.active || c.id === b.courtId)
          .map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
      </select>{' '}
      <CampoOra aria-label="Dalle" value={inizio} onChange={setInizio} />–
      <CampoOra aria-label="Alle" value={fine} onChange={setFine} />{' '}
      <button
        type="button"
        disabled={inCorso || !pronto}
        onClick={() => salva({ courtId: campo, startsAtTime: inizio, endsAtTime: fine })}
      >
        Salva
      </button>{' '}
      <button type="button" className="piatto" disabled={inCorso} onClick={lascia}>
        Lascia com’è
      </button>
    </span>
  );
}

/** 🧩 Una riga di prima, senza campo: «da completare», con la scelta del campo accanto. */
function SceltaDelCampo({
  b,
  campi,
  inCorso,
  scegli,
}: {
  b: BandaMaestro;
  campi: CampoDiGioco[];
  inCorso: boolean;
  scegli(courtId: string): void;
}): React.JSX.Element {
  const [campo, setCampo] = useState('');
  return (
    <span className="da-completare">
      {ora(b.startsAtTime)}–{ora(b.endsAtTime)} · <strong>da completare: scegli il campo</strong>{' '}
      <select aria-label="Campo" value={campo} onChange={(e) => setCampo(e.target.value)}>
        <option value="">Campo…</option>
        {campi
          .filter((c) => c.active)
          .map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
      </select>{' '}
      <button type="button" disabled={inCorso || campo === ''} onClick={() => scegli(campo)}>
        Riserva
      </button>
    </span>
  );
}

/**
 * ⭐ «RISERVA ALTRE ORE»: ogni settimana su più giorni in un colpo (le caselle e
 * le scorciatoie), oppure un giorno solo. Il campo è obbligatorio.
 *
 * ⚠️ Ogni settimana è una scrittura per giorno, perché un'ora riservata È una
 * riga per giorno: un giorno può essere rifiutato (già riservato a un altro
 * maestro) mentre gli altri passano, e l'esito si dice giorno per giorno.
 */
function RiservaAltreOre({ base, campi }: { base: string; campi: CampoDiGioco[] }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [modo, setModo] = useState<'settimana' | 'giorno'>('settimana');
  const [giorni, setGiorni] = useState<number[]>([6]);
  const [giorno, setGiorno] = useState('');
  const [campo, setCampo] = useState('');
  const [inizio, setInizio] = useState('');
  const [fine, setFine] = useState('');

  const quando = modo === 'settimana' ? giorni.length > 0 : giorno !== '';
  const pronto = quando && campo !== '' && inizio !== '' && fine !== '' && fine > inizio;
  // Nell'ordine in cui si LEGGONO (lunedì…domenica): l'esito scorre come le caselle.
  const scelti = ORDINE_SETTIMANA.filter((g) => giorni.includes(g));

  const riserva = useMutation({
    meta: { fatto: (d) => perGiorni(d as EsitoDelGiorno[], 'riserva delle ore') },
    mutationFn: async (): Promise<EsitoDelGiorno[]> => {
      const comune = { courtId: campo, startsAtTime: inizio, endsAtTime: fine };
      if (modo === 'giorno') {
        await chiama('POST', base, { ...comune, weekday: null, onDate: giorno });
        return [{ giorno: new Date(`${giorno}T00:00:00Z`).getUTCDay(), errore: null }];
      }
      const esiti: EsitoDelGiorno[] = [];
      for (const g of scelti) {
        try {
          await chiama('POST', base, { ...comune, weekday: g, onDate: null });
          esiti.push({ giorno: g, errore: null });
        } catch (e) {
          esiti.push({ giorno: g, errore: (e as Error).message });
        }
      }
      return esiti;
    },
    onSuccess: (esiti) => {
      rileggi();
      // Si svuotano le ORE: chi riserva scrive spesso mattina e pomeriggio sugli stessi giorni.
      if (esiti.every((e) => e.errore === null)) {
        setInizio('');
        setFine('');
      }
    },
  });

  const esiti = riserva.data ?? [];
  const scritte = esiti.filter((e) => e.errore === null).length;
  const rifiutati = esiti.filter((e) => e.errore !== null);
  const perMotivo = [
    ...new Map<string, number[]>(
      rifiutati.map((e) => [e.errore!, rifiutati.filter((x) => x.errore === e.errore).map((x) => x.giorno)]),
    ),
  ];

  return (
    <form
      className="pannello-fascia"
      onSubmit={(e) => {
        e.preventDefault();
        if (pronto) riserva.mutate();
      }}
    >
      <h4>Riserva altre ore</h4>
      <div className="scelta-a-bottoni" role="group" aria-label="Quando">
        <button
          type="button"
          className={modo === 'settimana' ? 'scelto' : undefined}
          aria-pressed={modo === 'settimana'}
          onClick={() => setModo('settimana')}
        >
          Ogni settimana
        </button>
        <button
          type="button"
          className={modo === 'giorno' ? 'scelto' : undefined}
          aria-pressed={modo === 'giorno'}
          onClick={() => setModo('giorno')}
        >
          Un giorno solo
        </button>
      </div>

      {modo === 'settimana' ? (
        <fieldset className="scelta-giorni">
          <legend>Giorni</legend>
          <div className="giorni-scelta">
            {ORDINE_SETTIMANA.map((i) => (
              <label key={i} className="campo orizzontale">
                <input
                  type="checkbox"
                  disabled={riserva.isPending}
                  checked={giorni.includes(i)}
                  onChange={(e) =>
                    setGiorni((p) => (e.target.checked ? [...p, i] : p.filter((g) => g !== i)))
                  }
                />
                <span>{GIORNI[i]}</span>
              </label>
            ))}
          </div>
          {/* ⭐ Le scorciatoie riempiono le caselle: dopo «Lun–ven» se ne può togliere una. */}
          <div className="scorciatoie">
            {GRUPPI_DI_GIORNI.map((g) => (
              <button
                key={g.nome}
                type="button"
                className="piatto"
                disabled={riserva.isPending}
                onClick={() => setGiorni([...g.giorni])}
              >
                {g.nome}
              </button>
            ))}
            <button
              type="button"
              className="piatto"
              disabled={riserva.isPending || giorni.length === 0}
              onClick={() => setGiorni([])}
            >
              Nessuno
            </button>
          </div>
        </fieldset>
      ) : (
        <CampoData etichetta="Giorno" value={giorno} min={oggi()} onChange={setGiorno} />
      )}

      <div className="riga-campi">
        <label className="campo">
          <span>Campo</span>
          <select required value={campo} onChange={(e) => setCampo(e.target.value)}>
            <option value="">Scegli il campo</option>
            {campi.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.active ? '' : ' (spento)'}
              </option>
            ))}
          </select>
        </label>
        <CampoOra etichetta="Dalle" value={inizio} onChange={setInizio} />
        <CampoOra etichetta="Alle" value={fine} onChange={setFine} />
      </div>

      {esiti.length > 0 && (
        <p
          className={rifiutati.length > 0 ? 'guasto' : 'tenue piccolo'}
          role={rifiutati.length > 0 ? 'alert' : undefined}
        >
          {scritte > 0 && (
            <>
              {scritte === 1 ? 'Un giorno riservato' : `${scritte} giorni riservati`}
              {rifiutati.length > 0 ? '. ' : '.'}
            </>
          )}
          {perMotivo.map(([motivo, giorniDelMotivo]) => (
            <span key={motivo}>
              {giorniDelMotivo.map((g) => GIORNI[g]).join(', ')}: {motivo}{' '}
            </span>
          ))}
        </p>
      )}
      <Guasto errore={riserva.error} />

      <button type="submit" disabled={riserva.isPending || !pronto}>
        {riserva.isPending
          ? 'Un attimo…'
          : modo === 'settimana' && scelti.length > 1
            ? `Riserva su ${scelti.length} giorni`
            : 'Riserva'}
      </button>
    </form>
  );
}

/** Com'è andata su UN giorno: un'ora riservata è una riga per giorno, e così l'esito. */
interface EsitoDelGiorno {
  giorno: number;
  errore: string | null;
}
